import { Suspense, lazy, useEffect } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { ErrorBoundary } from "react-error-boundary";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import posthog from "posthog-js";
import { reportError } from "@/app/_utils/reportError";
import AppThemeProvider from "@/app/_utils/providers/AppThemeProvider";
import { SessionAuthProvider } from "@/app/_utils/sessionClient";
import SessionToStoreSync from "@/app/_utils/providers/SessionToStoreSync";
import { CartProvider } from "@/app/_utils/providers/CartProvider";
import ScrollToTop from "@/app/_utils/ScrollToTop";

function PostHogPageView() {
  const location = useLocation();
  useEffect(() => {
    posthog.capture("$pageview");
  }, [location.pathname]);
  return null;
}

/** Eager public routes used from the main header — avoids Suspense spinner on every click. */
import HomePage from "@/app/(public)/home/page";
import NotFoundPage from "@/app/(public)/not-found/page";
import AboutPage from "@/app/(public)/about/page";
import ContactRedirectPage from "@/app/(public)/contact/page";
import PrivateEventsPage from "@/app/(public)/private-events/page";
import PrivateEntertainmentPage from "@/app/(public)/private-entertainment/page";
import LegacyTicketRedirect from "@/app/(public)/ticket/LegacyTicketRedirect";
import FaqPage from "@/app/(public)/faq/page";
import GalleryLayout from "@/app/(public)/gallery/layout";
import GalleryPage from "@/app/(public)/gallery/page";
import WorkWithUsPage from "@/app/(public)/work-with-us/page";
import TicketsPage from "@/app/(public)/tickets/page";
import PressPage from "@/app/(public)/press/page";
import NewsletterPage from "@/app/(public)/newsletter/page";
import MagicPage from "@/app/(public)/magic/page";
import CartPage from "@/app/(public)/cart/page";
import FestivalSlugPage from "@/app/(public)/festivals/[slug]/page";
import PartnerCampaignSlugPage from "@/app/(public)/partners/[slug]/page";
import BiltPortalPage from "@/app/(public)/partners/bilt-portal/BiltPortalPage";
import ProposalSlugPage from "@/app/(public)/proposals/[slug]/page";

const WaitlistPage = lazy(() => import("@/app/(public)/waitlist/page"));
const TicketPage = lazy(() => import("@/app/(public)/ticket/page"));
const SuccessPage = lazy(() => import("@/app/(public)/success/page"));
const CheckoutPage = lazy(() => import("@/app/(public)/checkout/page"));
const QueuePage = lazy(() => import("@/app/(public)/queue/[showId]/page"));
const TourPage = lazy(() => import("@/app/(public)/tour/page"));
const BundleTicketingDemoPage = lazy(() => import("@/app/(public)/tickets/bundle-demo/page"));
const ProductSlugPage = lazy(() => import("@/app/(public)/products/[slug]/page"));
const MerchHubPage = lazy(() => import("@/app/(public)/merch/page"));
const MerchProductPage = lazy(() => import("@/app/(public)/merch/[groupId]/page"));
const PublicLinkPage = lazy(() => import("@/app/(public)/links/[linkId]/page"));


function PageFallback() {
  return (
    <div
      style={{
        display: "flex",
        minHeight: "50vh",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <CircularProgress size={40} />
    </div>
  );
}

function RootErrorFallback() {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        gap: 16,
        padding: 24,
        textAlign: "center",
      }}
    >
      <Typography variant="h6" fontWeight={600}>
        Something went wrong
      </Typography>
      <Typography variant="body2" color="text.secondary">
        An unexpected error occurred. Please try refreshing the page.
      </Typography>
      <Button variant="outlined" onClick={() => window.location.reload()}>
        Reload
      </Button>
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary
      FallbackComponent={RootErrorFallback}
      onError={(error) => reportError(error, "root")}
    >
    <SessionAuthProvider>
      <SessionToStoreSync />
      <AppThemeProvider>
        <CartProvider>
          <ScrollToTop />
          <PostHogPageView />
          <Suspense fallback={<PageFallback />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/home" element={<Navigate to="/" replace />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactRedirectPage />} />
              <Route path="/private-events" element={<PrivateEventsPage />} />
              <Route path="/sc-holdings" element={<PrivateEntertainmentPage />} />
              <Route path="/faq" element={<FaqPage />} />
              <Route
                path="/gallery"
                element={
                  <GalleryLayout>
                    <GalleryPage />
                  </GalleryLayout>
                }
              />
              <Route path="/waitlist" element={<WaitlistPage />} />
              <Route path="/work-with-us" element={<WorkWithUsPage />} />
              <Route path="/tickets" element={<TicketsPage />} />
              <Route path="/tickets/bundle-demo" element={<BundleTicketingDemoPage />} />
              <Route path="/ticket" element={<TicketPage />} />
              {/* Legacy buy-one-ticket page. Its checkout endpoint is gone and every
                  purchase goes through the cart now, but these URLs were shared publicly
                  and still serve OG previews from share_preview_service. */}
              <Route path="/ticket/:id" element={<LegacyTicketRedirect />} />
              <Route path="/success" element={<SuccessPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/queue/:showId" element={<QueuePage />} />
              <Route path="/press" element={<PressPage />} />
              <Route path="/newsletter" element={<NewsletterPage />} />
              <Route path="/newsletter/:slug" element={<NewsletterPage />} />
              <Route path="/magic" element={<MagicPage />} />
              <Route path="/tour" element={<TourPage />} />
              <Route path="/products/:slug" element={<ProductSlugPage />} />
              <Route path="/merch" element={<MerchHubPage />} />
              <Route path="/merch/:groupId" element={<MerchProductPage />} />
              <Route path="/festivals/:slug" element={<FestivalSlugPage />} />
              <Route path="/links/:linkId" element={<PublicLinkPage />} />
              <Route path="/partners/bilt" element={<BiltPortalPage />} />
              <Route path="/partners/bilt/" element={<BiltPortalPage />} />
              <Route
                path="/partners/:campaignSlug"
                element={<PartnerCampaignSlugPage />}
              />
              <Route
                path="/partners/:campaignSlug/"
                element={<PartnerCampaignSlugPage />}
              />
              <Route path="/proposals/:slug" element={<ProposalSlugPage />} />
              <Route path="/proposals/:slug/" element={<ProposalSlugPage />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </CartProvider>
      </AppThemeProvider>
    </SessionAuthProvider>
    </ErrorBoundary>
  );
}
